import React, { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { 
  Truck, MapPin, Calendar, Coffee, 
  ChefHat, Loader2, Sun, Moon,
  UtensilsCrossed, ShieldAlert, ZoomIn, ZoomOut, RotateCcw, Info
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Map image URL
const MAP_IMAGE_URL = "https://customer-assets.emergentagent.com/job_fleet-manager-378/artifacts/y1ria6vs_WhatsApp%20Image%202026-03-12%20at%2013.29.47.jpeg";

// Zone colors for markers
const ZONE_MARKER_COLORS = {
  "Volley": { bg: "#FEF08A", border: "#FACC15", text: "#854D0E" },
  "Central": { bg: "#A7F3D0", border: "#10B981", text: "#065F46" },
  "Foot": { bg: "#BFDBFE", border: "#3B82F6", text: "#1E40AF" },
  "Hall d'honneur": { bg: "#FED7AA", border: "#F97316", text: "#9A3412" },
  "BAT S": { bg: "#E9D5FF", border: "#A855F7", text: "#6B21A8" },
  "Bars": { bg: "#FECACA", border: "#EF4444", text: "#991B1B" },
};

// Predefined zone positions on the map (percentages)
const ZONE_POSITIONS = {
  "Foot": { x: 30, y: 15 },
  "Volley": { x: 75, y: 45 },
  "Central": { x: 55, y: 80 },
  "Hall d'honneur": { x: 50, y: 95 },
  "BAT S": { x: 15, y: 60 },
  "Bars": { x: 35, y: 95 },
};

const HECClientViewPage = () => {
  const { planningId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  
  const [planning, setPlanning] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Map states
  const mapRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [positions, setPositions] = useState({});
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [viewService, setViewService] = useState("midi");

  useEffect(() => {
    const fetchPlanning = async () => {
      if (!token) {
        setError("Token d'accès manquant");
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${API_URL}/api/hec/plannings/${planningId}/client-view?token=${token}`
        );
        
        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.detail || "Erreur de chargement");
        }
        
        setPlanning(data);
        
        // Set initial selected day
        if (data.planning_days?.length > 0) {
          setSelectedDay(data.planning_days[0].date);
        } else if (data.date_debut) {
          setSelectedDay(data.date_debut);
        }
      } catch (err) {
        setError(err.message);
        toast.error(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPlanning();
  }, [planningId, token]);

  // Initialize positions when planning or day changes
  useEffect(() => {
    if (planning && selectedDay) {
      initializePositions();
    }
  }, [planning, selectedDay, viewService]);

  const initializePositions = () => {
    // First, check if there are saved positions from the planning
    if (planning?.map_positions && Object.keys(planning.map_positions).length > 0) {
      // Use saved positions
      setPositions(planning.map_positions);
      return;
    }
    
    // Otherwise, calculate default positions based on zones
    const slots = getSlots();
    const newPositions = {};
    const zoneCounters = {};

    slots.forEach((slot) => {
      const zone = slot.zone || "Central";
      const zonePos = ZONE_POSITIONS[zone] || { x: 50, y: 50 };
      
      // Offset each food truck in the same zone
      if (!zoneCounters[zone]) zoneCounters[zone] = 0;
      const offset = zoneCounters[zone] * 3;
      zoneCounters[zone]++;

      const key = getPositionKey(slot);
      newPositions[key] = {
        x: Math.min(95, Math.max(5, zonePos.x + (offset % 3) * 5 - 5)),
        y: Math.min(95, Math.max(5, zonePos.y + Math.floor(offset / 3) * 5)),
      };
    });

    setPositions(prev => ({ ...prev, ...newPositions }));
  };

  const getSlots = () => {
    if (!planning || !selectedDay) return [];
    
    const dayData = planning.planning_days?.find(d => d.date === selectedDay);
    if (!dayData) return [];
    
    // Return slots with their original service type for correct position mapping
    if (viewService === "midi") {
      const slots = [];
      (dayData.matin || []).forEach((slot, idx) => {
        slots.push({ ...slot, _service: "matin", _index: idx });
      });
      (dayData.midi || []).forEach((slot, idx) => {
        slots.push({ ...slot, _service: "midi", _index: idx });
      });
      (dayData.apres_midi || []).forEach((slot, idx) => {
        slots.push({ ...slot, _service: "apres_midi", _index: idx });
      });
      return slots;
    } else if (viewService === "soir") {
      const slots = [];
      (dayData.soir || []).forEach((slot, idx) => {
        slots.push({ ...slot, _service: "soir", _index: idx });
      });
      (dayData.nuit || []).forEach((slot, idx) => {
        slots.push({ ...slot, _service: "nuit", _index: idx });
      });
      return slots;
    }
    return [];
  };

  // Get the position key that matches how admin saves positions
  const getPositionKey = (slot) => {
    return `${selectedDay}-${slot._service}-${slot._index}`;
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), "EEE d MMM", { locale: fr });
    } catch {
      return dateStr;
    }
  };

  const getZoneColors = (zone) => {
    return ZONE_MARKER_COLORS[zone] || { bg: "#E5E7EB", border: "#9CA3AF", text: "#374151" };
  };

  const getFoodTruckDetails = (ftName) => {
    return planning?.food_trucks?.find(ft => 
      ft.nom === ftName || ft.name === ftName
    );
  };

  const getZoneStats = () => {
    const slots = getSlots();
    const stats = {};
    
    Object.keys(ZONE_MARKER_COLORS).forEach(zone => {
      stats[zone] = { count: 0, trucks: [] };
    });
    
    slots.forEach(slot => {
      const zone = slot.zone || "Central";
      if (!stats[zone]) stats[zone] = { count: 0, trucks: [] };
      stats[zone].count++;
      stats[zone].trucks.push(slot.food_truck_nom);
    });
    
    return stats;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-amber-600 mx-auto mb-4" />
          <p className="text-gray-600">Chargement du planning...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <ShieldAlert className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-800 mb-2">Accès refusé</h2>
            <p className="text-gray-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!planning) return null;

  const slots = getSlots();
  const zoneStats = getZoneStats();
  const planningDays = planning.planning_days || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100">
      {/* Header - Mobile responsive */}
      <header className="bg-gradient-to-r from-amber-700 to-orange-600 text-white py-3 px-3 sm:py-4 sm:px-4 shadow-lg">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
            <div className="flex items-center gap-2 sm:gap-3">
              <Truck className="w-6 h-6 sm:w-8 sm:h-8 flex-shrink-0" />
              <div className="min-w-0">
                <h1 className="text-base sm:text-xl font-bold truncate">{planning.nom_evenement || "Planning HEC"}</h1>
                <p className="text-amber-100 text-xs sm:text-sm truncate">{planning.lieu}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-amber-100 text-xs sm:text-sm">
              <Calendar className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
              <span className="whitespace-nowrap">{planning.date_debut} - {planning.date_fin}</span>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-2 sm:p-4">
        {/* Controls - Mobile responsive */}
        <Card className="mb-3 sm:mb-4">
          <CardContent className="py-2 sm:py-3 px-2 sm:px-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-4">
              {/* Day selector - Horizontal scroll on mobile */}
              <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0 -mx-1 px-1">
                <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 flex-shrink-0" />
                <div className="flex gap-1">
                  {planningDays.map((day) => (
                    <Button
                      key={day.date}
                      variant={selectedDay === day.date ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSelectedDay(day.date)}
                      className={`whitespace-nowrap text-xs sm:text-sm px-2 sm:px-3 ${selectedDay === day.date ? "bg-amber-600 hover:bg-amber-700" : ""}`}
                    >
                      {formatDate(day.date)}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Service & Zoom controls - Row on mobile */}
              <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-4">
                {/* Service selector */}
                <Select value={viewService} onValueChange={setViewService}>
                  <SelectTrigger className="w-32 sm:w-40 text-xs sm:text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="midi">
                      <div className="flex items-center gap-2">
                        <Sun className="w-4 h-4 text-yellow-500" />
                        <span className="hidden sm:inline">Journée (Midi)</span>
                        <span className="sm:hidden">Midi</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="soir">
                      <div className="flex items-center gap-2">
                        <Moon className="w-4 h-4 text-indigo-500" />
                        <span className="hidden sm:inline">Soirée</span>
                        <span className="sm:hidden">Soir</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>

                {/* Zoom controls */}
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" className="h-8 w-8 p-0 sm:h-9 sm:w-9" onClick={() => setZoom(z => Math.max(0.5, z - 0.25))}>
                    <ZoomOut className="w-3 h-3 sm:w-4 sm:h-4" />
                  </Button>
                  <span className="text-xs sm:text-sm text-gray-600 w-10 sm:w-12 text-center">{Math.round(zoom * 100)}%</span>
                  <Button variant="outline" size="sm" className="h-8 w-8 p-0 sm:h-9 sm:w-9" onClick={() => setZoom(z => Math.min(2, z + 0.25))}>
                    <ZoomIn className="w-3 h-3 sm:w-4 sm:h-4" />
                  </Button>
                  <Button variant="outline" size="sm" className="h-8 w-8 p-0 sm:h-9 sm:w-9 hidden sm:flex" onClick={() => setZoom(1)}>
                    <RotateCcw className="w-3 h-3 sm:w-4 sm:h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Map - Mobile responsive - Scrollable in portrait mode */}
        <Card className="mb-3 sm:mb-4 overflow-hidden">
          <CardContent className="p-0">
            <div 
              ref={mapRef}
              className="relative overflow-auto bg-gray-100 touch-pan-x touch-pan-y"
              style={{ height: "55vh", minHeight: "320px" }}
            >
              <div 
                className="relative transition-transform duration-200 inline-block"
                style={{ 
                  transform: `scale(${zoom})`,
                  transformOrigin: "top left",
                  minWidth: "max-content"
                }}
              >
                {/* Map Image - Maintains aspect ratio, scrollable on mobile */}
                <img 
                  src={MAP_IMAGE_URL} 
                  alt="Plan du campus HEC"
                  className="h-auto"
                  style={{ minWidth: "600px", maxWidth: "100vw" }}
                  draggable={false}
                />

                {/* Food Truck Markers - Smaller on mobile */}
                {slots.map((slot, index) => {
                  const key = getPositionKey(slot);
                  const pos = positions[key] || { x: 50, y: 50 };
                  const zone = slot.zone || "Central";
                  const colors = getZoneColors(zone);
                  const ftDetails = getFoodTruckDetails(slot.food_truck_nom);

                  return (
                    <div
                      key={key}
                      className="absolute cursor-pointer transform -translate-x-1/2 -translate-y-1/2 transition-all duration-200 hover:scale-110 hover:z-50 active:scale-110"
                      style={{
                        left: `${pos.x}%`,
                        top: `${pos.y}%`,
                        zIndex: 10
                      }}
                      onClick={() => {
                        setSelectedMarker({ ...slot, details: ftDetails });
                        setShowDetailDialog(true);
                      }}
                    >
                      <div 
                        className="relative group"
                        style={{
                          backgroundColor: colors.bg,
                          borderColor: colors.border,
                          color: colors.text
                        }}
                      >
                        {/* Marker - Smaller on mobile */}
                        <div 
                          className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 sm:border-3 flex items-center justify-center shadow-lg"
                          style={{
                            backgroundColor: colors.bg,
                            borderColor: colors.border,
                            borderWidth: "3px"
                          }}
                        >
                          <Truck className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: colors.text }} />
                        </div>

                        {/* Coffee badge */}
                        {ftDetails?.propose_cafe && (
                          <div className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-4 h-4 sm:w-5 sm:h-5 bg-amber-800 rounded-full flex items-center justify-center shadow">
                            <Coffee className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />
                          </div>
                        )}

                        {/* Tooltip on hover - Hidden on mobile (use tap instead) */}
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-gray-900 text-white text-xs rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none hidden sm:block">
                          <div className="font-bold">{slot.food_truck_nom}</div>
                          <div className="text-gray-300">{slot.specialite || ftDetails?.specialite}</div>
                          <div className="text-gray-400 mt-1 flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {zone}
                          </div>
                          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full">
                            <div className="border-8 border-transparent border-t-gray-900"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Legend & Zone Stats - Stack on mobile */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
          {/* Legend */}
          <Card>
            <CardHeader className="py-2 sm:py-3 px-3 sm:px-4">
              <CardTitle className="text-xs sm:text-sm flex items-center gap-2">
                <Info className="w-3 h-3 sm:w-4 sm:h-4" />
                Légende des zones
              </CardTitle>
            </CardHeader>
            <CardContent className="py-2 px-3 sm:px-4">
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {Object.entries(ZONE_MARKER_COLORS).map(([zone, colors]) => (
                  <Badge 
                    key={zone}
                    variant="outline"
                    className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5"
                    style={{
                      backgroundColor: colors.bg,
                      borderColor: colors.border,
                      color: colors.text
                    }}
                  >
                    {zone}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Zone Stats */}
          <Card>
            <CardHeader className="py-2 sm:py-3 px-3 sm:px-4">
              <CardTitle className="text-xs sm:text-sm flex items-center gap-2">
                <Truck className="w-3 h-3 sm:w-4 sm:h-4" />
                Répartition ({slots.length} food trucks)
              </CardTitle>
            </CardHeader>
            <CardContent className="py-2 px-3 sm:px-4">
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {Object.entries(zoneStats).filter(([_, data]) => data.count > 0).map(([zone, data]) => {
                  const colors = getZoneColors(zone);
                  return (
                    <Badge 
                      key={zone}
                      className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5"
                      style={{
                        backgroundColor: colors.bg,
                        borderColor: colors.border,
                        color: colors.text
                      }}
                    >
                      {zone}: {data.count}
                    </Badge>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Food Trucks List - Mobile responsive grid */}
        <Card>
          <CardHeader className="py-3 px-3 sm:px-6">
            <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
              <UtensilsCrossed className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
              Food Trucks présents - {viewService === "midi" ? "Journée" : "Soirée"}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3">
              {slots.map((slot, index) => {
                const ftDetails = getFoodTruckDetails(slot.food_truck_nom);
                const colors = getZoneColors(slot.zone);
                
                return (
                  <div 
                    key={index}
                    className="p-2.5 sm:p-3 rounded-lg border hover:shadow-md transition-shadow cursor-pointer active:shadow-md"
                    style={{ borderColor: colors.border, backgroundColor: `${colors.bg}30` }}
                    onClick={() => {
                      setSelectedMarker({ ...slot, details: ftDetails });
                      setShowDetailDialog(true);
                    }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-gray-800 text-sm sm:text-base flex items-center gap-1 truncate">
                          <span className="truncate">{slot.food_truck_nom}</span>
                          {ftDetails?.propose_cafe && (
                            <Coffee className="w-3 h-3 sm:w-4 sm:h-4 text-amber-700 flex-shrink-0" />
                          )}
                        </h4>
                        <p className="text-xs sm:text-sm text-gray-600 truncate">{slot.specialite || ftDetails?.specialite}</p>
                      </div>
                      <Badge 
                        variant="outline" 
                        className="text-[10px] sm:text-xs shrink-0 px-1.5 sm:px-2"
                        style={{ backgroundColor: colors.bg, borderColor: colors.border, color: colors.text }}
                      >
                        {slot.zone}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detail Dialog - Full width on mobile */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="w-[95vw] max-w-lg sm:w-full mx-auto max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sm sm:text-base">
              <Truck className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
              <span className="truncate">{selectedMarker?.food_truck_nom}</span>
              {selectedMarker?.details?.propose_cafe && (
                <Badge className="bg-amber-100 text-amber-800 ml-1 sm:ml-2 text-[10px] sm:text-xs">
                  <Coffee className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5 sm:mr-1" /> Café
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          {selectedMarker && (
            <div className="space-y-3 sm:space-y-4">
              {/* Zone & Speciality */}
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                <Badge 
                  className="text-[10px] sm:text-xs"
                  style={{
                    backgroundColor: getZoneColors(selectedMarker.zone).bg,
                    borderColor: getZoneColors(selectedMarker.zone).border,
                    color: getZoneColors(selectedMarker.zone).text
                  }}
                >
                  <MapPin className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5 sm:mr-1" />
                  {selectedMarker.zone}
                </Badge>
                {selectedMarker.specialite && (
                  <Badge variant="outline" className="text-[10px] sm:text-xs">
                    <ChefHat className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5 sm:mr-1" />
                    {selectedMarker.specialite}
                  </Badge>
                )}
              </div>

              {/* Menu */}
              {selectedMarker.details?.menu_par_jour && (
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2 text-xs sm:text-sm">
                    <UtensilsCrossed className="w-3 h-3 sm:w-4 sm:h-4" />
                    Menu
                  </h4>
                  <div className="space-y-1 max-h-40 sm:max-h-48 overflow-y-auto">
                    {Object.entries(selectedMarker.details.menu_par_jour).map(([jour, items]) => (
                      <div key={jour}>
                        {items && items.length > 0 && items.map((item, idx) => (
                          <div key={idx} className="flex justify-between items-center py-1 px-2 bg-gray-50 rounded text-xs sm:text-sm">
                            <span className="truncate mr-2">{item.nom}</span>
                            {item.prix > 0 && (
                              <Badge variant="outline" className="text-[10px] sm:text-xs flex-shrink-0">
                                {item.prix}€
                              </Badge>
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HECClientViewPage;
